import getSelectedTableViewId from 'mini-mail-list-store/lib/utils/getSelectedTableViewId';
import React, { useCallback, useRef } from 'react';
import RsvpUnitWrapper from './RsvpUnitWrapper';
import { CalendarEvent } from 'mini-calendar-event-types';
import { deleteMailListRow } from '../utils/deleteMailListRow';
import { deleteMeetingItemInMailListRow } from '../utils/deleteMeetingItemInMailListRow';
import { format } from 'owa-strings';
import { getFirstItemIdInMailListRow } from 'mini-mail-list-store/lib/utils/getFirstItemIdInMailListRow';
import { getMeetingClientItem } from 'mini-mail-list-store/lib/utils/getMeetingClientItem';
import { getOwaMiniStrings } from 'owa-strings/owa-mini-strings';
import { getRsvpUnitSubtitle } from 'mini-rsvp/lib/utils/getRsvpUnitSubtitle';
import { getRsvpUnitTitle } from 'mini-rsvp/lib/utils/getRsvpUnitTitle';
import { getTimestamp, now } from 'owa-datetime';
import { ListViewRsvpUnitProps } from '../schema/ListViewRsvpUnit.types';
import { logRsvpRenderDatapoint } from '../utils/logRsvpRenderDatapoint';
import { MAIL_LIST_VIEW } from 'data/lib/mini-app-constants/commonEventSourceConstants';
import { observer } from 'mobx-react-lite';
import { removeItemServiceProvider } from '../utils/removeItemServiceProvider';
import { RsvpRenderState } from '../schema/RsvpRenderState.types';
import { setRsvpUnitAriaDetailsForMailListRow } from 'mini-mail-list-store/lib/utils/setRsvpUnitAriaDetailsForMailListRow';

const ListViewRsvpUnit = (props: ListViewRsvpUnitProps) => {
    const { mailListRowId, listViewItemRenderTime: initialRenderTimestamp } = props;
    const firstItemId = getFirstItemIdInMailListRow(mailListRowId);
    const meetingClientItem = getMeetingClientItem(firstItemId);

    const strings = getOwaMiniStrings();

    // Calculate the time taken to lazy load List view rsvp unit
    const lazyLoadTime = useRef(getTimestamp(now()) - initialRenderTimestamp?.current);

    const fetchConflictingMeetingsFinished =
        (meetingClientItem as any)?.ConflictingMeetingCount !== undefined;
    const rsvpPartialRenderTime = useRef(null);
    const previousRsvpRenderState = useRef<RsvpRenderState>(RsvpRenderState.ShowPlaceholder);

    const logRsvpRenderDatapoints = useCallback(
        (calEvent: CalendarEvent) =>
            logRsvpRenderDatapoint(
                calEvent,
                meetingClientItem,
                lazyLoadTime.current,
                initialRenderTimestamp?.current,
                previousRsvpRenderState,
                rsvpPartialRenderTime,
                fetchConflictingMeetingsFinished
            ),
        [fetchConflictingMeetingsFinished, meetingClientItem]
    );

    const setRsvpUnitAriaDetails = (calEvent: CalendarEvent) => {
        const eventTime = getRsvpUnitTitle(calEvent);
        const conflictInfo = getRsvpUnitSubtitle(calEvent, meetingClientItem);
        const rsvpUnitAriaDetails = format(strings.meetingInfo, eventTime, conflictInfo);
        setRsvpUnitAriaDetailsForMailListRow(
            getSelectedTableViewId(),
            mailListRowId,
            rsvpUnitAriaDetails
        );
    };

    const removeCanceledEvent = useCallback(() => {
        deleteMailListRow(mailListRowId, false /** shouldIssueDeleteServiceCall */);
        return removeItemServiceProvider(meetingClientItem)();
    }, [meetingClientItem, mailListRowId]);

    return (
        <RsvpUnitWrapper
            tabIndex={-1}
            isActionable
            meetingClientItem={meetingClientItem}
            onRsvpActionSuccess={(actionType, updateOrganizer, message, scope, proposedNewTime) =>
                deleteMeetingItemInMailListRow(mailListRowId)
            }
            source={MAIL_LIST_VIEW}
            onConversionToCalEvent={(calEvent: CalendarEvent) => {
                logRsvpRenderDatapoints(calEvent);
                setRsvpUnitAriaDetails(calEvent);
            }}
            removeCanceledEvent={removeCanceledEvent}
        />
    );
};

export default observer(ListViewRsvpUnit);
